/**
 * Token store — fuente única de verdad de la sesión JWT del cliente.
 *
 * Reemplaza el manejo de sesión de `supabase.auth` (GoTrue). Persiste el
 * par de tokens + el usuario en localStorage y expone un pub/sub para que
 * los consumidores (hook `useSession`, store de carrito) reaccionen a
 * cambios de auth — equivalente a `supabase.auth.onAuthStateChange`.
 *
 * Eventos emitidos (compatibles con el switch del listener de carrito):
 *   - 'SIGNED_IN'         login / register / boot con sesión viva.
 *   - 'SIGNED_OUT'        logout / refresh fallido / expiración.
 *   - 'TOKEN_REFRESHED'   rotación de tokens (mismo usuario).
 *   - 'USER_UPDATED'      cambio de datos del usuario.
 */

import type { TokenPair } from './types';

export interface SessionUser {
  id: string;
  email: string;
  is_admin?: boolean;
  is_active?: boolean;
}

export interface AuthSession {
  user: SessionUser;
  tokens: TokenPair;
}

export type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED';

type AuthListener = (event: AuthEvent, session: AuthSession | null) => void;

const STORAGE_KEY = 'fc-commerce-auth';

let currentSession: AuthSession | null = null;
const listeners = new Set<AuthListener>();
let hydrated = false;

function readPersisted(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthSession>;
    if (
      parsed &&
      parsed.user &&
      typeof parsed.user.id === 'string' &&
      parsed.tokens &&
      typeof parsed.tokens.access_token === 'string'
    ) {
      return parsed as AuthSession;
    }
  } catch {
    /* storage corrupto / private mode — sesión nula */
  }
  return null;
}

function persist(session: AuthSession | null): void {
  try {
    if (session) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* quota / private mode — sesión degrada a sólo-memoria */
  }
}

function ensureHydrated(): void {
  if (hydrated) return;
  hydrated = true;
  currentSession = readPersisted();
}

function emit(event: AuthEvent): void {
  for (const listener of listeners) {
    try {
      listener(event, currentSession);
    } catch {
      /* un listener que tira no debe romper a los demás */
    }
  }
}

/** Sesión actual (hidrata desde storage en la primera lectura). */
export function getSession(): AuthSession | null {
  ensureHydrated();
  return currentSession;
}

/** Access token actual, o null si no hay sesión. */
export function getAccessToken(): string | null {
  return getSession()?.tokens.access_token ?? null;
}

/** Refresh token actual, o null si no hay sesión. */
export function getRefreshToken(): string | null {
  return getSession()?.tokens.refresh_token ?? null;
}

/** Establece una sesión nueva (login / register). Emite SIGNED_IN. */
export function setSession(session: AuthSession): void {
  ensureHydrated();
  const isNewUser = currentSession?.user.id !== session.user.id;
  currentSession = session;
  persist(session);
  emit(isNewUser ? 'SIGNED_IN' : 'USER_UPDATED');
}

/** Actualiza sólo el par de tokens (rotación). Emite TOKEN_REFRESHED. */
export function updateTokens(tokens: TokenPair): void {
  ensureHydrated();
  if (!currentSession) return;
  currentSession = { ...currentSession, tokens };
  persist(currentSession);
  emit('TOKEN_REFRESHED');
}

/** Actualiza sólo los datos del usuario. Emite USER_UPDATED. */
export function updateUser(user: SessionUser): void {
  ensureHydrated();
  if (!currentSession) return;
  currentSession = { ...currentSession, user };
  persist(currentSession);
  emit('USER_UPDATED');
}

/** Limpia la sesión (logout / refresh fallido). Emite SIGNED_OUT. */
export function clearSession(): void {
  ensureHydrated();
  if (!currentSession) return;
  currentSession = null;
  persist(null);
  emit('SIGNED_OUT');
}

/**
 * Suscribe a cambios de auth. Devuelve un unsubscribe.
 * Equivalente a `supabase.auth.onAuthStateChange(...).subscription.unsubscribe`.
 */
export function onAuthStateChange(listener: AuthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
